<template>
  <div ref="slider" class="image-slider" @mousedown="startDrag">
    <div ref="slides" class="slides" :style="slideStyle">
      <div v-for="(item, index) in images" :key="index" @click="handleImageClick($event, index)">
        <img class="p cover" :key="index" :src="item.imgurl" :style="getImageStyle(index)" alt="" ref="img" />
        <div class="t">微观：{{ item.label }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageSlider',
  props: {
    images: {
      type: Array,
      required: true,
      validator(value) {
        return value.every(item =>
          typeof item === 'object' && typeof item.imgurl === 'string'
        );
      }
    },
    imgWidth: {
      type: String,
      required: true
    },
    imgHeight: {
      type: String,
      default: 'auto'
    },
    gap: {
      type: String,
      default: '0px'
    }
  },
  data() {
    return {
      currentX: 0,
      containerWidth: 0,
      totalContentWidth: 0,
      parsedGap: 0,
      canDrag: false,

      // 拖拽状态
      isDragging: false,
      startX: 0,
      startY: 0, // 新增：记录起始 Y，防止垂直滚动误判
      startCurrentX: 0,
      currentDragX: 0,
      dragDistance: 0, // 记录拖动距离
      dragThreshold: 5 // 像素阈值，小于则视为点击
    };
  },
  computed: {
    slideStyle() {
      return {
        transform: `translateX(${this.currentX}px)`
      };
    },
    minTranslateX() {
      return Math.min(0, -(this.totalContentWidth - this.containerWidth));
    }
  },
  methods: {
    getImageStyle(index) {
      const style = {
        width: this.imgWidth,
        height: this.imgHeight,
        display: 'block',
        flexShrink: 0,
        cursor: 'pointer' // 可选：提示可点击
      };
      if (index < this.images.length - 1) {
        style.marginRight = this.gap;
      }
      return style;
    },

    startDrag(e) {
      if (e.button !== 0 || !this.canDrag) return;

      this.startX = e.clientX;
      this.startY = e.clientY; // 记录 Y
      this.startCurrentX = this.currentX;
      this.isDragging = false; // 初始为 false，移动后才设为 true
      this.dragDistance = 0;

      document.addEventListener('mousemove', this.onDrag, { passive: false });
      document.addEventListener('mouseup', this.endDrag, { passive: true });
      document.addEventListener('mouseleave', this.endDrag, { passive: true });

      // 防止选中
      document.body.classList.add('no-select');
      e.preventDefault();
    },

    onDrag(e) {
      const deltaX = e.clientX - this.startX;
      const deltaY = e.clientY - this.startY;
      this.dragDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // 只有移动超过阈值才视为拖拽
      if (this.dragDistance > this.dragThreshold) {
        if (!this.isDragging) {
          this.isDragging = true;
          // 如果之前有 preventDefault，这里已生效
        }

        let newX = this.startCurrentX + deltaX;
        if (newX > 0) newX = 0;
        else if (newX < this.minTranslateX) newX = this.minTranslateX;

        this.currentDragX = newX;
        this.$refs.slides.style.transform = `translateX(${newX}px)`;
      }
    },

    endDrag(e) {
      // 清理监听
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.endDrag);
      document.removeEventListener('mouseleave', this.endDrag);

      document.body.classList.remove('no-select');

      if (this.isDragging) {
        // 拖拽结束：同步位置
        let finalX = this.currentDragX;
        if (finalX > 0) finalX = 0;
        else if (finalX < this.minTranslateX) finalX = this.minTranslateX;
        this.currentX = finalX;
        this.$refs.slides.style.transform = '';
      }

      // 重置状态
      this.isDragging = false;
    },

    handleImageClick(e, index) {
      // ✅ 关键：如果刚结束拖拽，阻止点击
      if (this.dragDistance > this.dragThreshold) {
        // 阻止点击行为（拖拽产生的 click）
        e.stopPropagation();
        e.preventDefault();
        return;
      }

      // 正常点击
      this.$emit('image-click', { index, item: this.images[index] });
    },

    parsePx(str) {
      const match = str.match(/^(\d+(\.\d+)?)px$/);
      return match ? parseFloat(match[1]) : 0;
    },

    updateDimensions() {
      this.$nextTick(() => {
        const container = this.$refs.slider;
        if (!container) return;

        this.containerWidth = container.offsetWidth;
        this.parsedGap = this.parsePx(this.gap);

        const imgs = this.$refs.img || [];
        const imgEls = Array.isArray(imgs) ? imgs : [imgs];
        this.totalContentWidth = imgEls.reduce((sum, img, i) => {
          return sum + img.offsetWidth + (i < imgEls.length - 1 ? this.parsedGap : 0);
        }, 0);

        this.canDrag = this.totalContentWidth > this.containerWidth;

        if (this.currentX > 0) {
          this.currentX = 0;
        } else if (this.currentX < this.minTranslateX) {
          this.currentX = this.minTranslateX;
        }
      });
    }
  },

  mounted() {
    this.updateDimensions();
    window.addEventListener('resize', this.updateDimensions, { passive: true });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateDimensions);
    document.body.classList.remove('no-select');
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.endDrag);
    document.removeEventListener('mouseleave', this.endDrag);
  }
};
</script>

<style scoped lang="scss">
/* 全局禁止选中（拖动时） */
.no-select {
  user-select: none !important;
  -webkit-user-select: none !important;
}

.image-slider {
  // width: 100%;
  overflow: hidden;
  cursor: grab;
  position: relative;
}

.image-slider:active {
  cursor: grabbing;
}

.slides {
  display: flex;
  transition: none;
  /* 拖动时无过渡 */
  will-change: transform;

  /* contain: strict; */
  /* 提示浏览器隔离渲染 */
  .p {
    border-radius: 0px;
  }

  .t {
    text-align: center;
    font-family: SourceHanSansCN;
    font-weight: 400;
    font-size: 20px;
    margin-top: 16px;
  }
}

/* 拖动时进一步优化：禁用子元素交互 */
.slides.dragging img {
  pointer-events: none;
}
</style>